import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../api";
import Avatar from "./Avatar";
import NotifBell from "./NotifBell";
import { useNotifs } from "./NotifContext";

function Layout() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { push: pushNotif } = useNotifs();
  const [pendingCount, setPendingCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [pageKey, setPageKey] = useState(location.pathname);

  // Transition de page
  useEffect(() => {
    setPageKey(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };

    // Solde restant dans la sidebar
    axios.get(API + "/leaves/mine", { headers })
      .then(leavesRes => {
        axios.get(API + "/users/balance", { headers })
          .then(balRes => {
            const total = parseFloat(balRes.data.cp_total) || 30;
            const poses = leavesRes.data.filter(l => l.statut === "approved" && l.type === "CP").reduce((s, l) => s + parseFloat(l.jours || 0), 0);
            setBalance(Math.round((total - poses) * 10) / 10);
          });
      });

    const loadCount = () => {
      if (user.role === "associe" || user.role === "daf") {
        axios.get(API + "/leaves/team", { headers })
          .then(r => {
            const count = r.data.filter(l => {
              if (l.cancel_requested) return true;
              if (l.statut !== "pending") return false;
              // Le DAF ne compte que les demandes dont il est responsable (assistantes)
              if (user.role === "daf") {
                return Array.isArray(l.approvals) && l.approvals.some(a => a.associe_id === user.id && a.statut === "pending");
              }
              // Associé : ne plus compter les demandes que j'ai déjà validées (en attente d'un autre responsable)
              return !(Array.isArray(l.approvals) && l.approvals.some(a => a.associe_id === user.id && a.statut !== "pending"));
            }).length;
            setPendingCount(prev => {
              if (count > prev && prev > 0) pushNotif(`${count - prev} nouvelle(s) demande(s) à valider`, "warning");
              return count;
            });
          });
      }
    };

    loadCount();
    const interval = setInterval(loadCount, 60000); // polling toutes les 60s

    const handler = (e) => {
      if (e.detail && e.detail.decrement) setPendingCount(c => Math.max(0, c - 1));
      else loadCount();
    };
    window.addEventListener('validationUpdated', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('validationUpdated', handler);
    };
  }, [token, user.role]);

  const handleLogout = async () => {
    try {
      await axios.post(API + "/auth/logout", {}, {
        headers: { Authorization: "Bearer " + token }
      });
    } catch {}
    logout();
    navigate("/login");
  };

  const handleNav = () => setSidebarOpen(false);

  const roleCfg = {
    daf:       { label: "DAF", color: "#7C6FE0", bg: "#EEEDFE" },
    associe:   { label: "Associé", color: "#185FA5", bg: "#E6F1FB" },
    assistante:{ label: "Assistante", color: "#854F0B", bg: "#FAEEDA" },
    collab:    { label: "Collaborateur", color: "#3B6D11", bg: "#EAF3DE" },
  }[user.role] || { label: user.role, color: "#888", bg: "#f0f0f0" };

  const validationLink = (
    <NavLink to="/validations" style={navStyle} onClick={handleNav}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>✅ Validations</span>
        {pendingCount > 0 && (
          <span style={{ background: "#A32D2D", color: "#fff", borderRadius: "50%", fontSize: 10, fontWeight: 700, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {pendingCount}
          </span>
        )}
      </div>
    </NavLink>
  );

  const sidebarContent = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo */}
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 28, paddingLeft: 4, display: "flex", alignItems: "center", gap: 8, color: "#7C6FE0", letterSpacing: "-0.3px" }}>
        <span style={{ fontSize: 20 }}>📅</span> Congés SWA
      </div>

      <NavLink to="/" end style={navStyle} onClick={handleNav}>🏠 Tableau de bord</NavLink>

      {(user.role === "collab" || user.role === "associe" || user.role === "daf" || user.role === "assistante") && (
        <NavLink to="/demande" style={navStyle} onClick={handleNav}>
          {(user.role === "associe" || user.role === "daf") ? "✏️ Saisir mes congés" : "➕ Nouvelle demande"}
        </NavLink>
      )}

      {(user.role === "collab" || user.role === "assistante") && (
        <>
          <NavLink to="/historique" style={navStyle} onClick={handleNav}>📋 Mes demandes</NavLink>
          <NavLink to="/tt" style={navStyle} onClick={handleNav}>💻 Mon TT</NavLink>
          <NavLink to="/planning" style={navStyle} onClick={handleNav}>👥 Planning équipe</NavLink>
          <NavLink to="/planning-global" style={navStyle} onClick={handleNav}>🏢 Planning cabinet</NavLink>
        </>
      )}

      {user.role === "associe" && (
        <>
          {validationLink}
          <NavLink to="/historique" style={navStyle} onClick={handleNav}>📋 Mes congés</NavLink>
          <NavLink to="/tt" style={navStyle} onClick={handleNav}>💻 Mon TT</NavLink>
          <NavLink to="/planning" style={navStyle} onClick={handleNav}>👥 Planning équipe</NavLink>
          <NavLink to="/planning-global" style={navStyle} onClick={handleNav}>🏢 Planning cabinet</NavLink>
          <NavLink to="/annuel" style={navStyle} onClick={handleNav}>📅 Vue annuelle</NavLink>
          <NavLink to="/soldes" style={navStyle} onClick={handleNav}>💰 Soldes équipe</NavLink>
        </>
      )}

      {user.role === "daf" && (
        <>
          {validationLink}
          <NavLink to="/historique" style={navStyle} onClick={handleNav}>📋 Mes congés</NavLink>
          <NavLink to="/tt" style={navStyle} onClick={handleNav}>💻 Mon TT</NavLink>
          <NavLink to="/absences" style={navStyle} onClick={handleNav}>🗓️ Mes jours non travaillés</NavLink>
          <NavLink to="/planning-global" style={navStyle} onClick={handleNav}>🏢 Planning cabinet</NavLink>
          <NavLink to="/annuel" style={navStyle} onClick={handleNav}>📅 Vue annuelle</NavLink>
          <NavLink to="/admin" style={navStyle} onClick={handleNav}>⚙️ Administration</NavLink>

          <NavLink to="/audit" style={navStyle} onClick={handleNav}>📜 Journal d'activité</NavLink>
        </>
      )}

      {/* Spacer */}
      <div style={{ marginTop: "auto" }}>
        {/* Solde restant */}
        {balance !== null && (
          <div style={{
            background: balance <= 5 ? "#FCEBEB" : "#EAF3DE",
            border: `1px solid ${balance <= 5 ? "#F09595" : "#97C459"}`,
            borderRadius: 8, padding: "8px 12px", marginBottom: 10, textAlign: "center",
          }}>
            <div style={{ fontSize: 11, color: balance <= 5 ? "#A32D2D" : "#3B6D11", fontWeight: 600 }}>
              {balance <= 5 ? "⚠️ " : "✅ "}Solde CP
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: balance <= 5 ? "#A32D2D" : "#3B6D11", lineHeight: 1.2 }}>{balance} j</div>
            <div style={{ fontSize: 10, color: "#aaa", marginTop: 1 }}>restants</div>
          </div>
        )}

        <div style={{ height: 1, background: "#e8e8e8", margin: "8px 0 10px" }} />

        <NavLink to="/profil" style={navStyle} onClick={handleNav}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar prenom={user.prenom} nom={user.nom} size={28} fontSize={11} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>{user.prenom} {user.nom}</div>
              <span style={{ fontSize: 10, color: roleCfg.color, background: roleCfg.bg, padding: "1px 6px", borderRadius: 10, fontWeight: 500 }}>{roleCfg.label}</span>
            </div>
          </div>
        </NavLink>

        <button onClick={handleLogout} style={{ width: "100%", padding: "8px", background: "none", border: "1px solid #e8e8e8", borderRadius: 6, cursor: "pointer", fontSize: 12, color: "#888", marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <span>↩</span> Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "sans-serif" }}>
      {/* Sidebar desktop */}
      <div className="sidebar-desktop" style={{ width: 210, background: "#f8f8fb", borderRight: "1px solid #e8e8e8", display: "flex", flexDirection: "column", padding: "16px 10px", flexShrink: 0 }}>
        {sidebarContent}
      </div>

      {/* Sidebar mobile overlay */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100 }} />
      )}
      <div className={`sidebar-mobile${sidebarOpen ? " open" : ""}`} style={{
        position: "fixed", top: 0, left: sidebarOpen ? 0 : -230, width: 210, height: "100vh",
        background: "#f8f8fb", borderRight: "1px solid #e8e8e8",
        padding: "16px 10px", zIndex: 101, transition: "left 0.25s ease",
        overflowY: "auto",
      }}>
        {sidebarContent}
      </div>

      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        {/* Barre mobile */}
        <div className="mobile-topbar" style={{ display: "none", padding: "12px 16px", background: "#f8f8fb", borderBottom: "1px solid #e8e8e8", alignItems: "center", gap: 12 }}>
          <button onClick={() => setSidebarOpen(true)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", padding: 0 }}>☰</button>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#7C6FE0" }}>📅 Congés SWA</span>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <NotifBell />
            {pendingCount > 0 && (
              <span style={{ background: "#A32D2D", color: "#fff", borderRadius: "50%", fontSize: 11, fontWeight: 700, width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>{pendingCount}</span>
            )}
          </div>
        </div>

        {/* Topbar desktop — cloche + fil d'ariane */}
        <div className="sidebar-desktop" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "8px 20px", borderBottom: "1px solid #f0f0f0", background: "#fff", gap: 8 }}>
          <NotifBell />
        </div>

        {/* Contenu avec animation de transition */}
        <div key={pageKey} style={{ flex: 1, overflow: "auto", padding: 24, animation: "fadeInPage 0.25s ease" }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

const navStyle = ({ isActive }) => ({
  display: "block",
  padding: "8px 10px",
  marginBottom: 2,
  borderRadius: 6,
  textDecoration: "none",
  fontSize: 13,
  color: isActive ? "#7C6FE0" : "#444",
  background: isActive ? "#EEEDFE" : "none",
  fontWeight: isActive ? 600 : 400,
  transition: "background 0.15s, color 0.15s",
});

export default Layout;
